import * as Phaser from 'phaser';
import {
  CONTACT_BLOB_MIX,
  PROP_CONTACT_WIDTH_FACTOR,
  PROP_ORIGIN_X,
  PROP_ORIGIN_Y,
  contactBlobAxes,
  extrudeOffset,
  propDef,
  propDisplaySize,
  propOccludedByLand,
  resolveElevationStack,
  ySortDepth,
  type MapDocument,
} from '../sim/map/index.ts';

export type OverlayYSort = {
  base: number;
  span: number;
};

/**
 * Image pool for island props. Keys are `islandId:decorationId` so a redraw moves
 * existing sprites instead of allocating a new one per prop.
 *
 * Visible palms sit above every column so a tree in front of a cliff is not
 * clipped. Depth inside that band is world-y (`ySortDepth`) so a south plant
 * covers a north checkpoint pole — same rule as two palms. A palm whose base
 * is under a taller column is hidden whole — a sprite cannot be clipped to
 * the column silhouette.
 */
export class IslandOverlayPool {
  private readonly images = new Map<string, Phaser.GameObjects.Image>();
  private readonly blobs = new Map<string, Phaser.GameObjects.Graphics>();
  private readonly islandIds = new Map<string, string>();
  private readonly occluded = new Map<string, boolean>();
  private visibleIds: ReadonlySet<string> | null = null;
  private readonly scene: Phaser.Scene;
  private readonly ySort: OverlayYSort;

  constructor(scene: Phaser.Scene, ySort: OverlayYSort) {
    this.scene = scene;
    this.ySort = ySort;
  }

  /**
   * `elevate` follows the drawn column: play is always raised; the editor
   * passes false while View → Elevation is off so the sprite stays on the click.
   */
  sync(map: MapDocument, elevate = true): void {
    const seen = new Set<string>();
    const height = map.bounds.height;
    const stack = resolveElevationStack(map);

    for (const island of map.islands) {
      const world = stack.get(island.id)?.worldElevation ?? island.elevation;
      const offset = extrudeOffset(elevate ? world : 0);

      for (const decoration of island.decorations) {
        const key = `${island.id}:${decoration.id}`;
        seen.add(key);
        const texture = propDef(decoration.catalog).texture;
        let image = this.images.get(key);

        if (!image || !image.scene) {
          image = this.scene.add.image(decoration.x, decoration.y + offset.y, texture);
          image.setOrigin(PROP_ORIGIN_X, PROP_ORIGIN_Y);
          this.images.set(key, image);
        }

        const hidden = propOccludedByLand(map, island, decoration, elevate);
        const size = propDisplaySize(decoration.catalog, decoration.scale);
        const depth = ySortDepth(this.ySort.base, decoration.y, height, this.ySort.span);
        const shown = this.isShown(island.id, hidden);
        this.islandIds.set(key, island.id);
        this.occluded.set(key, hidden);
        image.setTexture(texture);
        image.setPosition(decoration.x, decoration.y + offset.y);
        image.setDisplaySize(size, size);
        image.setRotation(decoration.rotation);
        image.setDepth(depth);
        image.setVisible(shown);

        let blob = this.blobs.get(key);

        if (!blob || !blob.scene) {
          blob = this.scene.add.graphics();
          this.blobs.set(key, blob);
        }

        const axes = contactBlobAxes(size * PROP_CONTACT_WIDTH_FACTOR);
        blob.clear();
        blob.fillStyle(0x000000, CONTACT_BLOB_MIX);
        blob.fillEllipse(decoration.x, decoration.y + offset.y, axes.width, axes.height);
        blob.setDepth(depth - 0.001);
        blob.setVisible(shown);
      }
    }

    for (const [key, image] of this.images) {
      if (seen.has(key)) {
        continue;
      }

      image.destroy();
      this.blobs.get(key)?.destroy();
      this.images.delete(key);
      this.blobs.delete(key);
      this.islandIds.delete(key);
      this.occluded.delete(key);
    }
  }

  /**
   * Hide props whose island is off camera. Occlusion from `sync` still wins.
   * Until the first cull, every non-occluded prop stays visible.
   */
  applyVisibility(visibleIds: ReadonlySet<string>): void {
    this.visibleIds = visibleIds;

    for (const [key, image] of this.images) {
      const islandId = this.islandIds.get(key);
      const shown = this.isShown(islandId, this.occluded.get(key) === true);
      image.setVisible(shown);
      this.blobs.get(key)?.setVisible(shown);
    }
  }

  private isShown(islandId: string | undefined, occluded: boolean): boolean {
    if (occluded) {
      return false;
    }

    if (this.visibleIds === null || islandId === undefined) {
      return true;
    }

    return this.visibleIds.has(islandId);
  }

  destroy(): void {
    for (const image of this.images.values()) {
      image.destroy();
    }

    for (const blob of this.blobs.values()) {
      blob.destroy();
    }

    this.images.clear();
    this.blobs.clear();
    this.islandIds.clear();
    this.occluded.clear();
    this.visibleIds = null;
  }
}
